import { useContext } from "react";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { scheduleCrud } from "@/api/schedule";
import { Button } from "@/components/common/Button";
import { ModalAlert } from "@/components/common/modals";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useModal } from "@/hooks/common/use-modal";
import { showToast } from "@/providers";
import { useUserStore } from "@/stores/use-user-store";
import { canManageSchedulePause } from "@/utils/features/schedule/permission";
import { getUserRoleByDepartment, isProducerUnavailableInDepartment } from "@/utils/user-positions";

import { Icon } from "@/components/common/Icon";
import { ScheduleActionsContext } from "../Provider";

export const ScheduleActionsUnpause = () => {
  const { schedule } = useContext(ScheduleActionsContext);
  const { department } = useDepartmentRoute();
  const activeUser = useUserStore((state) => state.activeUser);
  const modal = useModal();
  const queryClient = useQueryClient();
  const activeRole = getUserRoleByDepartment(activeUser, department);

  const mutation = useMutation({
    mutationFn: () => scheduleCrud.unpause(schedule.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["schedule"], refetchType: "all" });
      modal.closeModal();
      showToast({ type: "success", title: "Atendimento retomado!" });
    },
    onError: () =>
      showToast({
        type: "error",
        title: "Erro ao retomar atendimento.",
        content: "Tente novamente em alguns instantes.",
      }),
  });

  if (
    isProducerUnavailableInDepartment(activeUser, department) ||
    !canManageSchedulePause(activeRole, activeUser?.id, schedule, "PAUSED")
  ) {
    return null;
  }

  return (
    <>
      <Button leading={<Icon name="PlayArrow" fill />} variant="secondary" onClick={modal.openModal} full>
        Retomar atendimento
      </Button>
      <ModalAlert
        isOpen={modal.isModalOpen}
        onClose={mutation.isPending ? undefined : modal.closeModal}
        isOverlayCloseable={!mutation.isPending}
        cancelable
        title="Retomar atendimento?"
        description='Ao confirmar, o status do atendimento voltará para "Em atendimento"'
        actions={[
          {
            type: "submit",
            children: "Retomar atendimento",
            variant: "primary",
            isLoading: mutation.isPending,
            onClick: () => mutation.mutate(),
          },
        ]}
      />
    </>
  );
};
